import {
  messages,
  ZStream,
  zlibDeflateInit2,
  zlibDeflateSetDictionary,
  zlibDeflate,
  zlibDeflateEnd
} from './zlib.mjs';
import type { Z_CallStatus, Z_FlushMode } from './zlib.mjs';
import { flattenChunks } from './utils.ts';

const toString = Object.prototype.toString;

/* Public constants ==========================================================*/
/* ===========================================================================*/

import {
  Z_NO_FLUSH, Z_SYNC_FLUSH, Z_FULL_FLUSH, Z_FINISH,
  Z_OK, Z_STREAM_END, Z_STREAM_ERROR,
  Z_DEFAULT_COMPRESSION,
  Z_DEFAULT_STRATEGY,
  Z_DEFLATED
} from './zlib/constants.mjs';

/* ===========================================================================*/

/** @inline */
type DeflateInput = Uint8Array | ArrayBuffer | string;

interface DeflateOptions {
  /**
   * Compression level. See the
   * [zlib manual](http://zlib.net/manual.html#Advanced) for more information.
   * @group zlib options
   */
  level?: number;
  /**
   * Size of generated data chunks (16K by default).
   * @group Extensions
   */
  chunkSize?: number;
  /**
   * Window size. See the
   * [zlib manual](http://zlib.net/manual.html#Advanced) for more information.
   * @group zlib options
   */
  windowBits?: number;
  /**
   * Memory level. See the
   * [zlib manual](http://zlib.net/manual.html#Advanced) for more information.
   * @group zlib options
   */
  memLevel?: number;
  /**
   * Compression strategy. See the
   * [zlib manual](http://zlib.net/manual.html#Advanced) for more information.
   * @group zlib options
   */
  strategy?: number;
  /**
   * Do raw deflate. Say that we work with raw stream, if you don't wish to
   * specify negative `windowBits` implicitly.
   * @group Extensions
   */
  raw?: boolean;
  /**
   * Create gzip wrapper.
   * @group Extensions
   */
  gzip?: boolean;
  /**
   * Initial dictionary. See the
   * [zlib manual](http://zlib.net/manual.html#Advanced) for more information.
   * @group zlib options
   */
  dictionary?: Uint8Array | ArrayBuffer;
  /**
   * Set to `true` to use the classic zlib hash, which matches canonical zlib
   * output byte-for-byte. The default `false` uses the faster ANZAC++ hash,
   * which matches recent (chromium) node.js output instead.
   * @group Extensions
   */
  legacyHash?: boolean;
}

const defaultOptions: Required<DeflateOptions> = {
  level: Z_DEFAULT_COMPRESSION,
  chunkSize: 16384,
  windowBits: 15,
  memLevel: 8,
  strategy: Z_DEFAULT_STRATEGY,
  raw: false,
  gzip: false,
  legacyHash: false,
  dictionary: new Uint8Array(0)
};

/**
 * Generic JS-style wrapper for zlib calls. If you don't need
 * streaming behaviour, use the simpler functions {@link deflate},
 * {@link deflateRaw} and {@link gzip}.
 */
class Deflate {
  private options: Required<DeflateOptions>;

  /**
   * Error code after deflate finishes. {@link Z_OK} on success.
   * You will not need it in real life, because deflate errors
   * are possible only on wrong options or bad custom `onData` / `onEnd`
   * handlers.
   */
  err: Z_CallStatus;

  /** Error message, if {@link Deflate.err} is not {@link Z_OK}. */
  msg: string;

  private ended: boolean;
  private started: boolean;

  /**
   * Chunks of output data, if {@link Deflate.onData} not overridden.
   * @internal
   */
  chunks: Uint8Array<ArrayBuffer>[];

  private strm: ZStream;

  /**
   * Compressed result, generated by default {@link Deflate.onData}
   * and {@link Deflate.onEnd} handlers. Filled after you push last chunk
   * (call {@link Deflate.push} with {@link Z_FINISH} / `true` param).
   */
  result: Uint8Array<ArrayBuffer>;

  /**
   * Creates a new deflator instance with the specified params. Throws an
   * exception on bad params. See {@link DeflateOptions} for the list of
   * supported options.
   *
   * @example
   * ```javascript
   * import { Deflate } from 'pako'
   *
   * const chunk1 = new Uint8Array([1, 2, 3, 4, 5, 6, 7, 8, 9])
   * const chunk2 = new Uint8Array([10, 11, 12, 13, 14, 15, 16, 17, 18, 19])
   *
   * const deflate = new Deflate({ level: 3 })
   *
   * deflate.push(chunk1, false)
   * deflate.push(chunk2, true)  // true -> last chunk
   *
   * if (deflate.err) throw new Error(deflate.err)
   *
   * console.log(deflate.result)
   * ```
   */
  constructor(options: DeflateOptions = {}) {
    this.options = Object.assign({}, defaultOptions, options);

    const opt = this.options;

    if (opt.raw && (opt.windowBits > 0)) {
      opt.windowBits = -opt.windowBits;
    }

    else if (opt.gzip && (opt.windowBits > 0) && (opt.windowBits < 16)) {
      opt.windowBits += 16;
    }

    this.err    = Z_OK;   // error code, if happens
    this.msg    = '';     // error message
    this.ended  = false;  // used to avoid multiple onEnd() calls
    this.started = false; // used to call onStart() only once
    this.chunks = [];     // chunks of compressed data
    this.result = new Uint8Array(0);

    this.strm = new ZStream();
    this.strm.avail_out = 0;

    let status = zlibDeflateInit2(
      this.strm,
      opt.level,
      Z_DEFLATED,
      opt.windowBits,
      opt.memLevel,
      opt.strategy,
      opt.legacyHash
    );

    if (status !== Z_OK) {
      throw new Error(messages[status]);
    }

    if (toString.call(opt.dictionary) === '[object ArrayBuffer]') {
      opt.dictionary = new Uint8Array(opt.dictionary as ArrayBuffer);
    }

    const dictionary = opt.dictionary as Uint8Array;

    if (dictionary.length) {
      if (opt.gzip) {
        throw new Error('dictionary is not supported with gzip');
      }

      status = zlibDeflateSetDictionary(this.strm, dictionary);

      if (status !== Z_OK) {
        throw new Error(messages[status]);
      }
    }
  }

  /**
   * Sends input data to the deflate pipe, generating {@link Deflate.onData} calls
   * with new compressed chunks. Returns `true` on success. The last data block must
   * have `flush_mode` {@link Z_FINISH} (or `true`). That will flush the internal
   * pending buffers and call {@link Deflate.onEnd}.
   *
   * On failure, calls {@link Deflate.onEnd} with the error code and returns false.
   *
   * @param data input data. Strings will be converted to utf8 byte sequence.
   * @param flush_mode 0..6 for corresponding {@link Z_NO_FLUSH}..{@link Z_TREES} modes.
   *   See constants. Skipped or `false` means {@link Z_NO_FLUSH}, `true` means {@link Z_FINISH}.
   *
   * @example
   * ```javascript
   * push(chunk, false) // push one of data chunks
   * ...
   * push(chunk, true)  // push last chunk
   * ```
   */
  push(data: DeflateInput, flush_mode: Z_FlushMode | boolean = false): boolean {
    const strm = this.strm;
    const chunkSize = this.options.chunkSize;
    let status: Z_CallStatus;
    let _flush_mode: Z_FlushMode;

    if (this.ended) { return false; }

    if (typeof flush_mode === 'number') _flush_mode = flush_mode;
    else _flush_mode = flush_mode === true ? Z_FINISH : Z_NO_FLUSH;

    // Convert data if needed
    if (typeof data === 'string') {
      // If we need to compress text, change encoding to utf8.
      strm.input = new TextEncoder().encode(data);
    } else if (toString.call(data) === '[object ArrayBuffer]') {
      strm.input = new Uint8Array(data as ArrayBuffer);
    } else {
      strm.input = data as Uint8Array;
    }

    strm.next_in = 0;
    strm.avail_in = strm.input.length;

    if (!this.started) {
      this.started = true;
      this.onStart(strm);
    }

    for (;;) {
      if (strm.avail_out === 0) {
        strm.output = new Uint8Array(chunkSize);
        strm.next_out = 0;
        strm.avail_out = chunkSize;
      }

      // Make sure avail_out > 6 to avoid repeating markers
      if ((_flush_mode === Z_SYNC_FLUSH || _flush_mode === Z_FULL_FLUSH) && strm.avail_out <= 6) {
        this.onData(strm.output.subarray(0, strm.next_out));
        strm.avail_out = 0;
        continue;
      }

      status = zlibDeflate(strm, _flush_mode);

      // Hard error (e.g. an unsupported flush mode): stop instead of looping forever.
      if (status === Z_STREAM_ERROR) break;

      // Ended => flush the tail and finalize with the deflateEnd status.
      if (status === Z_STREAM_END) {
        if (strm.next_out > 0) {
          this.onData(strm.output.subarray(0, strm.next_out));
        }
        status = zlibDeflateEnd(this.strm);
        break;
      }

      // Flush if out buffer full
      if (strm.avail_out === 0) {
        this.onData(strm.output);
        continue;
      }

      // Flush if requested and has data
      if (_flush_mode > 0 && strm.next_out > 0) {
        this.onData(strm.output.subarray(0, strm.next_out));
        strm.avail_out = 0;
        continue;
      }

      // Input exhausted but stream not ended - the only non-finalizing exit:
      // don't touch err/msg/result, don't call onEnd, more input may follow.
      if (strm.avail_in === 0) return true;
    }

    this.err = status;
    this.msg = strm.msg || messages[status];
    this.ended = true;
    this.onEnd(status);

    return status === Z_OK;
  }


  /**
   * Called once before the first low-level deflate call.
   */
  onStart(strm: ZStream): void {}


  /**
   * By default, stores data blocks in the {@link Deflate.chunks} property and glues
   * them in {@link Deflate.onEnd}. Override this handler if you need another behaviour.
   */
  onData(chunk: Uint8Array<ArrayBuffer>): void {
    this.chunks.push(chunk);
  }


  /**
   * Called once after you tell deflate that the input stream is
   * complete ({@link Z_FINISH}). By default, joins the collected {@link Deflate.chunks}
   * into the {@link Deflate.result} property.
   *
   * @param status deflate status. {@link Z_OK} on success, other if not.
   */
  onEnd(status: Z_CallStatus): void {
    // On success - join collected chunks
    if (status === Z_OK) {
      this.result = flattenChunks(this.chunks);
    }
    this.chunks = [];
  }
}


/**
 * Compress `data` with deflate algorithm and `options`.
 * See {@link DeflateOptions} for the list of supported options.
 *
 * @example
 * ```javascript
 * import { deflate } from 'pako'
 *
 * const data = new Uint8Array([1, 2, 3, 4, 5, 6, 7, 8, 9])
 *
 * console.log(deflate(data))
 * ```
 */
function deflate(input: DeflateInput, options: DeflateOptions = {}): Uint8Array<ArrayBuffer> {
  const deflator = new Deflate(options);

  deflator.push(input, true);

  // That will never happen if you don't cheat with options :)
  if (deflator.err) { throw new Error(deflator.msg); }

  return deflator.result;
}


/**
 * The same as {@link deflate}, but creates raw data without a wrapper
 * (header and adler32 crc).
 */
function deflateRaw(input: DeflateInput, options: DeflateOptions = {}): Uint8Array<ArrayBuffer> {
  return deflate(input, Object.assign({}, options, { raw: true }));
}


/**
 * The same as {@link deflate}, but creates a gzip wrapper instead of
 * a deflate one.
 */
function gzip(input: DeflateInput, options: DeflateOptions = {}): Uint8Array<ArrayBuffer> {
  return deflate(input, Object.assign({}, options, { gzip: true }));
}


export { Deflate, deflate, deflateRaw, gzip };
export type { DeflateInput, DeflateOptions };
